import { motion } from "framer-motion";
import { Compass, MoonStar, ShieldCheck, ArrowRight } from "lucide-react";

const SOLUTIONS = [
  {
    id: "coaching",
    testid: "solution-card-lifeworks",
    ctaTestid: "cta-lifeworks",
    icon: Compass,
    tint: "bg-blue-700/10 text-blue-700",
    bar: "bg-blue-700",
    badge: "Life Coaching",
    heading: "Ready to Stop Drifting and Start Living on Purpose?",
    body: "Most people don't need more motivation. They need a map. The Life Mastery System is a step-by-step coaching framework that helps you clarify what you actually want, break the habits holding you back, and build daily practices that compound into real change.",
    bestFor: "Anyone feeling stuck, burned out, or ready for their next chapter.",
    cta: "Explore Life Mastery Coaching",
    ctaUrl: "#packages",
    external: false,
  },
  {
    id: "sleep-recovery",
    testid: "solution-card-nucalm",
    ctaTestid: "cta-nucalm",
    icon: MoonStar,
    tint: "bg-sky-600/10 text-sky-600",
    bar: "bg-sky-500",
    badge: "Sleep & Recovery",
    heading: "Deep Rest in Minutes, Not Hours",
    body: "You can't perform at a high level on a nervous system that never downshifts. NuCalm uses patented neuroscience to guide your brain into the restorative state usually reserved for deep sleep, in as little as 20 minutes. I use it daily, and it's changed how I recover, focus, and sleep.",
    bestFor: "High performers, poor sleepers, and anyone running on stress.",
    cta: "Learn How NuCalm Works",
    ctaUrl: "https://jonsie7927.xosialx.com/allaccesspass.html",
    external: true,
  },
  {
    id: "legal-protection",
    testid: "solution-card-legalshield",
    ctaTestid: "cta-legalshield",
    icon: ShieldCheck,
    tint: "bg-indigo-600/10 text-indigo-600",
    bar: "bg-indigo-600",
    badge: "Legal Protection",
    heading: "A Lawyer in Your Corner, Without the $350/Hour Bill",
    body: "Life throws legal questions at everyone: contracts, traffic tickets, wills, identity theft. LegalShield gives you and your family direct access to experienced provider law firms for a flat monthly fee. I partnered with them because peace of mind shouldn't be a luxury.",
    bestFor: "Families, small business owners, and anyone who wants to stop Googling legal questions at midnight.",
    cta: "See How LegalShield Works",
    ctaUrl: "https://johnjones.legalshieldassociate.com/",
    external: true,
  },
];

export default function Solutions() {
  return (
    <section data-testid="solutions-section" className="py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Pick the Door That's Holding You Back
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
            Each of these stands on its own. Together, they cover how you perform, how you recover,
            and how you stay protected.
          </p>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {SOLUTIONS.map((s, i) => (
            <motion.article
              key={s.id}
              id={s.id}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.12 }}
              className="group relative flex scroll-mt-28 flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-blue-900/10"
            >
              <span className={`h-1.5 w-full ${s.bar}`} />
              <div className="flex flex-1 flex-col p-7">
                <div className="flex items-center justify-between">
                  <span
                    className={`flex h-12 w-12 items-center justify-center rounded-2xl ${s.tint}`}
                  >
                    <s.icon className="h-6 w-6" />
                  </span>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    {s.badge}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-xl font-bold leading-snug text-slate-900">
                  {s.heading}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{s.body}</p>
                <p className="mt-5 rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
                  <span className="font-bold text-slate-800">Best for: </span>
                  {s.bestFor}
                </p>
                <a
                  href={s.ctaUrl}
                  {...(s.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  data-testid={s.ctaTestid}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl border-2 border-blue-700 px-5 py-3 text-sm font-bold text-blue-700 transition-all duration-200 group-hover:gap-3 hover:bg-blue-700 hover:text-white"
                >
                  {s.cta}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
